import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Col, Row, Skeleton } from 'antd'
import { Formik } from 'formik'

import getInvoices, { Invoice } from 'core/application/adminDashboard/companySettings/subscription/invoices'
import getSubscriptionDetails, {
  Subscription,
} from 'core/application/adminDashboard/companySettings/subscription/subscriptionDetails'

import HorizontalLine from 'common/components/dataDisplay/HorizontalLine'

import { queryRequest } from 'common/RequestUtils'
import { DefaultSelect } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'

import BillingInformation from './billingInformation'
import SubscriptionDetail from './components/SubscriptionDetail'
import SubscriptionPriceItem from './components/SubscriptionPriceItem'
import InvoicesTable from './invoicesTable'
import PaymentInformation from './paymentInformation'

const SubscriptionDetails: React.FC = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()
  const [subscriptionDetails, setSubscriptionDetails] = useState<Subscription | null>(null)

  const [loadingInvoices, setLoadingInvoices] = useState<boolean>(false)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [selectedYear, setSelectedYear] = useState(null)

  const [loading, setLoading] = useState<boolean>(false)

  const [tableWidth, setTableWidth] = useState(null)
  const tableContainerRef = useRef(null)

  const handleTableWidth = useCallback(() => {
    let width = tableContainerRef?.current?.offsetWidth || 0
    if (width) {
      setTableWidth(width)
    }
  }, [])

  useEffect(() => {
    handleTableWidth()

    window.addEventListener('resize', handleTableWidth)

    return () => {
      window.removeEventListener('resize', handleTableWidth)
    }
  }, [])

  const fetchData = async () => {
    setLoading(true)
    await fetchPaymentInfo()
    setLoading(false)
  }

  const fetchPaymentInfo = async () => {
    const data = await queryRequest(() => getSubscriptionDetails())
    setSubscriptionDetails(data)
    setSelectedYear(data.years[0])
  }

  const fetchInvoicesTable = async () => {
    setLoadingInvoices(true)
    const result = await queryRequest(() => getInvoices(selectedYear))
    setInvoices(result)
    setLoadingInvoices(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    console.log('selectedYear: ' + selectedYear)
    if (selectedYear) {
      fetchInvoicesTable()
    }
  }, [selectedYear])

  let invoiceTablePros = {
    invoices: invoices,
    loadingInvoices: loadingInvoices,
    history: history,
    ...(tableWidth ? { scroll: { x: tableWidth } } : {}),
  }

  return (
    <Skeleton loading={loading}>
      {subscriptionDetails && (
        <>
          <Row gutter={24}>
            <Col xs={12} md={6}>
              <SubscriptionDetail
                title={translate('admin-dashboard.company-settings.subscription.title')}
                description={subscriptionDetails.subscriptionPlan.toString()}
              />
            </Col>
            <Col xs={12} md={6}>
              <SubscriptionDetail
                title={translate('admin-dashboard.company-settings.subscription.renews-on')}
                description={parseZoneFormat(subscriptionDetails.renewsOn)}
              />
            </Col>
          </Row>

          <Row>
            <Col span={24} className="mb-16 mt-8">
              <HorizontalLine />
            </Col>
          </Row>

          <Row>
            <Col>
              <h6 className="text-semi-bold">
                {translate('admin-dashboard.company-settings.subscription.current-pricing')}
              </h6>
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} className="mt-16">
              <SubscriptionPriceItem
                title={
                  subscriptionDetails.subscriptionPlan.toString() === 'Annual'
                    ? translate('admin-dashboard.company-settings.subscription.annual-base-fee')
                    : translate('admin-dashboard.company-settings.subscription.monthly-base-fee')
                }
                description={
                  subscriptionDetails.subscriptionPlan.toString() === 'Annual'
                    ? translate('admin-dashboard.company-settings.subscription.annual-base-fee-description')
                    : translate('admin-dashboard.company-settings.subscription.monthly-base-fee-description')
                }
                price={subscriptionDetails.baseFee}
              />
            </Col>
          </Row>

          <Row>
            <Col xs={24} md={24} className="mt-24">
              <SubscriptionPriceItem
                title={
                  subscriptionDetails.subscriptionPlan.toString() === 'Annual'
                    ? translate('admin-dashboard.company-settings.subscription.annual-per-ee-fee')
                    : translate('admin-dashboard.company-settings.subscription.per-ee-fee')
                }
                description={
                  subscriptionDetails.subscriptionPlan.toString() === 'Annual'
                    ? translate('admin-dashboard.company-settings.subscription.annual-per-ee-fee-description')
                    : translate('admin-dashboard.company-settings.subscription.per-ee-fee-description')
                }
                price={subscriptionDetails.perEEFee}
              />
            </Col>
          </Row>

          <Row>
            <Col span={24} className="mb-16 mt-16">
              <HorizontalLine />
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <PaymentInformation
                paymentMethod={subscriptionDetails.paymentMethod}
                paymentDetails={subscriptionDetails.paymentDetails}
                onSave={fetchPaymentInfo}
              />
            </Col>
          </Row>

          <Row>
            <Col span={24} className="mb-16 mt-8">
              <HorizontalLine />
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <BillingInformation
                billingEmail={subscriptionDetails.billingEmail}
                onBillingInfoSave={fetchPaymentInfo}
              />
            </Col>
          </Row>

          <Row>
            <Col span={24} className="mb-16 mt-8">
              <HorizontalLine />
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <h6>{translate('admin-dashboard.company-settings.subscription.invoices')}</h6>
            </Col>
            <Col md={12} className="flex justify-end">
              <Formik
                initialValues={{ year: selectedYear }}
                onSubmit={() => {
                  console.log('onSubmit')
                }}
              >
                {(formik) => (
                  <form onSubmit={formik.handleSubmit}>
                    <DefaultSelect
                      name="year"
                      defaultValue={formik.values.year}
                      onChange={(year) => {
                        setSelectedYear(year)
                      }}
                      items={subscriptionDetails.years.map((x) => {
                        return {
                          name: x,
                          id: x,
                        }
                      })}
                    ></DefaultSelect>
                  </form>
                )}
              </Formik>
            </Col>
          </Row>
        </>
      )}
      <Row>
        <Col md={24} className="mt-16" ref={tableContainerRef}>
          <InvoicesTable {...invoiceTablePros} />
        </Col>
      </Row>
    </Skeleton>
  )
}

export default SubscriptionDetails
